import React from "react";
import { TrashIcon } from "../../SVG/trash-icon";
import { useCriteriaTreeBuilderContext } from "./criteria-tree-builder-context";
import { CriteriaGroup as CriteriaGroupType } from "./criteria-tree-builder-types";
import CriteriaStatementRow from "./CriteriaStatementRow";
import CriteriaConjunctionToggle from "./CriteriaConjunctionToggle";

export interface CriteriaGroupProps {
  group: CriteriaGroupType;
  isRoot?: boolean;
  /** 0-based depth from the root group. */
  depth?: number;
  onStatementChange: (statementId: string, statementValue: string) => void;
  onToggleConjunction: (groupId: string, conjunction: "AND" | "OR") => void;
  onAddCondition: (groupId: string) => void;
  onAddNestedGroup: (groupId: string) => void;
  onRemove: (nodeId: string) => void;
}

/**
 * Renders a conjunction group (AND/OR) and its children, recursively.
 * Internal to CriteriaTreeBuilder.
 */
const CriteriaGroup = ({
  group,
  isRoot = false,
  depth = 0,
  onStatementChange,
  onToggleConjunction,
  onAddCondition,
  onAddNestedGroup,
  onRemove,
}: CriteriaGroupProps) => {
  const { disabled, labels, allowNestedGroups, maxNestingDepth } =
    useCriteriaTreeBuilderContext();

  const hasChildren = group.children.length > 0;
  // A nested group may be added when nesting is enabled and (if bounded) the
  // current group is below the maximum depth.
  const canNest =
    allowNestedGroups && (maxNestingDepth === undefined || depth < maxNestingDepth);

  return (
    <div
      className={`criteria-tree-builder__group${isRoot ? "" : " criteria-tree-builder__group--nested"}${disabled ? " criteria-tree-builder__group--disabled" : ""}`}
    >
      <div className="criteria-tree-builder__group-header">
        <CriteriaConjunctionToggle
          value={group.conjunction}
          onChange={(conjunction) => onToggleConjunction(group.id, conjunction)}
          disabled={disabled}
        />
        {!isRoot && (
          <button
            type="button"
            onClick={() => onRemove(group.id)}
            className="criteria-tree-builder__delete"
            aria-label={labels.removeGroup}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
          >
            <TrashIcon size={18} />
          </button>
        )}
      </div>

      {(hasChildren || !disabled) && (
        <div
          className={`criteria-tree-builder__children${!disabled ? " criteria-tree-builder__children--has-actions" : ""}`}
        >
          <div className="criteria-tree-builder__connector criteria-tree-builder__connector--vertical" />
          <div className="criteria-tree-builder__children-list">
            {group.children.map((child) => (
              <div key={child.id} className="criteria-tree-builder__children__row">
                <div
                  className={`criteria-tree-builder__connector criteria-tree-builder__connector--horizontal ${
                    child.type === "statement"
                      ? "criteria-tree-builder__connector--statement"
                      : "criteria-tree-builder__connector--group"
                  }`}
                />
                <div className="criteria-tree-builder__child">
                  {child.type === "statement" ? (
                    <CriteriaStatementRow
                      statement={child}
                      onStatementChange={onStatementChange}
                      onRemove={onRemove}
                      depth={depth}
                    />
                  ) : (
                    <CriteriaGroup
                      group={child}
                      depth={depth + 1}
                      onStatementChange={onStatementChange}
                      onToggleConjunction={onToggleConjunction}
                      onAddCondition={onAddCondition}
                      onAddNestedGroup={onAddNestedGroup}
                      onRemove={onRemove}
                    />
                  )}
                </div>
              </div>
            ))}
            {!disabled && (
              <div className="criteria-tree-builder__children__row">
                <div className="criteria-tree-builder__connector criteria-tree-builder__connector--horizontal criteria-tree-builder__connector--actions" />
                <div className="criteria-tree-builder__child">
                  <div className="criteria-tree-builder__actions">
                    <button
                      type="button"
                      className="criteria-tree-builder__link-action"
                      onClick={() => onAddCondition(group.id)}
                    >
                      {labels.addCondition}
                    </button>
                    {canNest && (
                      <button
                        type="button"
                        className="criteria-tree-builder__link-action"
                        onClick={() => onAddNestedGroup(group.id)}
                      >
                        {labels.addNestedGroup}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CriteriaGroup;